import {TriangleDownIcon, type Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Tooltip} from '@primer/react'
import {memo, useCallback, useMemo} from 'react'
import {
  DockedPanel,
  useEnabledDockedPanels,
  useActiveDockedPanel,
  useDockedPanelActions,
} from '../../contexts/DockedPanelContext'
import styles from './Panel.module.css'
import {getPanelOpenMetadata, usePanelPresentation, usePanelSwitcherSelectCallback} from './panel-registry'

export function PanelNavigationDropdown() {
  const activePanel = useActiveDockedPanel()
  const enabledPanels = useEnabledDockedPanels()

  const currentValue = useMemo(() => {
    return activePanel || DockedPanel.Comments
  }, [activePanel])
  const showLabel = currentValue !== DockedPanel.CopilotChat
  const {heading: panelHeading, icon: panelIcon} = usePanelPresentation(currentValue)
  const trigger = (
    <Button
      aria-label={showLabel ? panelHeading : undefined}
      leadingVisual={panelIcon}
      trailingVisual={TriangleDownIcon}
      size="medium"
      variant="invisible"
      className={styles.PanelNavigationDropdown}
    >
      {showLabel ? panelHeading : null}
    </Button>
  )

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {showLabel ? (
          trigger
        ) : (
          <Tooltip text={panelHeading} type="label">
            {trigger}
          </Tooltip>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="start">
        <ActionList selectionVariant="single" showDividers={false}>
          {enabledPanels.map(value => (
            <PanelNavigationItem key={value} value={value} selected={value === currentValue} />
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

interface PanelNavigationItemProps {
  value: DockedPanel
  selected: boolean
}

const PanelNavigationItem = memo(function PanelNavigationItemComponent({value, selected}: PanelNavigationItemProps) {
  const {openPanel} = useDockedPanelActions()
  const {heading, icon} = usePanelPresentation(value)
  const onPanelSwitcherSelect = usePanelSwitcherSelectCallback(value)

  const handleSelect = useCallback(() => {
    const metadata = getPanelOpenMetadata(value)
    openPanel(value, metadata ? {metadata} : undefined)
    onPanelSwitcherSelect?.()
  }, [onPanelSwitcherSelect, openPanel, value])

  return <PanelNavigationActionListItem selected={selected} heading={heading} icon={icon} onSelect={handleSelect} />
})

interface PanelNavigationActionListItemProps {
  selected: boolean
  heading: string
  icon: Icon
  onSelect: () => void
}

function PanelNavigationActionListItem({
  selected,
  heading,
  icon: LeadingVisual,
  onSelect,
}: PanelNavigationActionListItemProps) {
  return (
    <ActionList.Item selected={selected} onSelect={onSelect}>
      <ActionList.LeadingVisual>
        <LeadingVisual />
      </ActionList.LeadingVisual>
      {heading}
    </ActionList.Item>
  )
}
